import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Center,
  Flex,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconArrowLeft,
  IconInbox,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import tableTools from '../../../global/helpers/tableTools.ts';
import { useGetHealthcareFacilities } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilities';
import { useGetTherapeuticIndications } from '../../../global/entities/TherapeuticIndication/queries/useGetTherapeuticIndications';
import { useGetQuestionnaires } from '../../../global/entities/Questionnaires/queries/useGetQuestionnaires';
import { useGetCarePlanTemplates } from '../../../global/entities/CarePlanTemplate/queries/useGetCarePlanTemplates';
import { useCreateCarePlanTemplate } from '../../../global/entities/CarePlanTemplate/mutations/useCreateCarePlanTemplate';
import { useUpdateCarePlanTemplate } from '../../../global/entities/CarePlanTemplate/mutations/useUpdateCarePlanTemplate';
import { useDeleteCarePlanTemplate } from '../../../global/entities/CarePlanTemplate/mutations/useDeleteCarePlanTemplate';
import { useSetCarePlanTemplateStatus } from '../../../global/entities/CarePlanTemplate/mutations/useSetCarePlanTemplateStatus';
import {
  CarePlanTemplateStatus,
  ICarePlanTaskInput,
  ICarePlanTemplate,
  ICarePlanTemplateInput,
} from '../../../global/entities/CarePlanTemplate/CarePlanTemplate';
import CarePlanTasksConfig from '../components/CarePlanTasksConfig';

interface CarePlanFormValues {
  nameEn: string;
  nameFr: string;
  planType: string;
  therapeuticIndicationID: string;
  clinicIds: string[];
  eventLabelEn: string;
  eventLabelFr: string;
  eventDateRequired: boolean;
  descriptionEn: string;
  descriptionFr: string;
  tasks: ICarePlanTaskInput[];
}

const emptyFormValues = (): CarePlanFormValues => ({
  nameEn: '',
  nameFr: '',
  planType: '',
  therapeuticIndicationID: '',
  eventLabelEn: '',
  eventLabelFr: '',
  eventDateRequired: false,
  clinicIds: [],
  descriptionEn: '',
  descriptionFr: '',
  tasks: [],
});

const isCarePlanTemplateStatus = (
  value: string | null,
): value is CarePlanTemplateStatus =>
  value === 'Draft' || value === 'Active' || value === 'Archived';

const CarePlanTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: templates, isLoading, isError } = useGetCarePlanTemplates();
  const { data: facilities } = useGetHealthcareFacilities();
  const { data: therapeuticIndications } = useGetTherapeuticIndications();
  const { data: questionnaires } = useGetQuestionnaires();

  const createTemplate = useCreateCarePlanTemplate();
  const updateTemplate = useUpdateCarePlanTemplate();
  const deleteTemplate = useDeleteCarePlanTemplate();
  const setStatus = useSetCarePlanTemplateStatus();

  const [editing, setEditing] = useState<ICarePlanTemplate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const form = useForm<CarePlanFormValues>({
    initialValues: emptyFormValues(),
    validate: {
      nameEn: (v) => (v.trim() ? null : t('carePlan.validation.nameRequired', 'Name is required')),
      nameFr: (v) => (v.trim() ? null : t('carePlan.validation.nameRequired', 'Name is required')),
      therapeuticIndicationID: (v) =>
        v ? null : t('carePlan.validation.therapeuticIndicationRequired', 'Therapeutic indication is required'),
    },
  });

  const indicationOptions = useMemo(
    () =>
      (therapeuticIndications ?? []).map((indication) => ({
        value: indication.id.toString(),
        label: indication.name,
      })),
    [therapeuticIndications],
  );

  const facilityOptions = useMemo(
    () =>
      (facilities ?? []).map((facility) => ({
        value: facility.id.toString(),
        label: facility.name,
      })),
    [facilities],
  );

  const clinicNames = (ids: number[]): string =>
    ids.length === 0
      ? t('carePlan.fields.allClinics', 'All clinics')
      : ids
          .map(
            (id) =>
              (facilities ?? []).find((facility) => facility.id === id)?.name ??
              `#${id}`,
          )
          .join(', ');

  const statusOptions = useMemo(
    () => [
      { value: 'Draft', label: t('carePlan.status.draft', 'Draft') },
      { value: 'Active', label: t('carePlan.status.active', 'Active') },
      { value: 'Archived', label: t('carePlan.status.archived', 'Archived') },
    ],
    [t],
  );

  const indicationName = (id: number): string =>
    (therapeuticIndications ?? []).find((indication) => indication.id === id)?.name ?? '';

  const openCreate = () => {
    setEditing(null);
    form.setValues(emptyFormValues());
    form.resetDirty();
    setIsFormOpen(true);
  };

  const openEdit = (template: ICarePlanTemplate) => {
    setEditing(template);
    form.setValues({
      nameEn: template.nameEn,
      nameFr: template.nameFr,
      planType: template.planType ?? '',
      therapeuticIndicationID: template.therapeuticIndicationID.toString(),
      clinicIds: template.healthcareFacilityIDs.map((id) => id.toString()),
      eventLabelEn: template.eventLabelEn ?? '',
      eventLabelFr: template.eventLabelFr ?? '',
      eventDateRequired: template.eventDateRequired,
      descriptionEn: template.descriptionEn ?? '',
      descriptionFr: template.descriptionFr ?? '',
      tasks: template.tasks.map((task) => ({
        questionnaireID: task.questionnaireID,
        schedulingPhase: task.schedulingPhase,
        availableDateOffsetDays: task.availableDateOffsetDays,
        dueDateOffsetDays: task.dueDateOffsetDays,
        isRecurring: task.isRecurring,
        recurrenceType: task.recurrenceType,
        recurrenceInterval: task.recurrenceInterval,
        recurrenceEndOffsetDays: task.recurrenceEndOffsetDays,
        expirationDays: task.expirationDays,
      })),
    });
    form.resetDirty();
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(null);
  };

  const toInput = (values: CarePlanFormValues): ICarePlanTemplateInput => ({
    healthcareFacilityIDs: values.clinicIds.map((id) => parseInt(id)),
    therapeuticIndicationID: parseInt(values.therapeuticIndicationID),
    planType: values.planType.trim() || undefined,
    nameEn: values.nameEn.trim(),
    nameFr: values.nameFr.trim(),
    descriptionEn: values.descriptionEn.trim() || undefined,
    descriptionFr: values.descriptionFr.trim() || undefined,
    eventLabelEn: values.eventLabelEn.trim() || undefined,
    eventLabelFr: values.eventLabelFr.trim() || undefined,
    eventDateRequired: values.eventDateRequired,
    tasks: values.tasks,
  });

  const handleSubmit = form.onSubmit((values) => {
    const input = toInput(values);

    if (editing) {
      updateTemplate.mutate(
        { id: editing.id, input },
        { onSuccess: () => closeForm() },
      );
      return;
    }

    createTemplate.mutate(input, {
      onSuccess: () => closeForm(),
    });
  });

  const handleDelete = (template: ICarePlanTemplate) => {
    modals.openConfirmModal({
      title: t('carePlan.deleteConfirmTitle', 'Delete care plan'),
      children: (
        <Text size="sm">
          {t('carePlan.deleteConfirmBody', 'Are you sure you want to delete "{{name}}"?', {
            name: template.nameEn,
          })}
        </Text>
      ),
      labels: {
        confirm: t('button.delete', 'Delete'),
        cancel: t('global.back', 'Back'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteTemplate.mutate(template.id),
    });
  };

  const handleStatusChange = (template: ICarePlanTemplate, status: CarePlanTemplateStatus) => {
    setStatus.mutate({ id: template.id, status });
  };

  if (isFormOpen) {
    return (
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={closeForm}>
              {t('global.back', 'Back')}
            </Button>
            <Title c="black" order={4}>
              {editing
                ? t('carePlan.editTitle', 'Edit care plan')
                : t('carePlan.createTitle', 'New care plan')}
            </Title>
          </Group>
        </Group>

        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Group grow align="flex-start">
              <TextInput
                label={t('carePlan.fields.nameEn', 'Name (EN)')}
                withAsterisk
                {...form.getInputProps('nameEn')}
              />
              <TextInput
                label={t('carePlan.fields.nameFr', 'Name (FR)')}
                withAsterisk
                {...form.getInputProps('nameFr')}
              />
            </Group>

            <Group grow align="flex-start">
              <MultiSelect
                label={t('carePlan.fields.clinics', 'Clinics')}
                description={t(
                  'carePlan.fields.clinicsHint',
                  'Leave empty to make the plan available to all clinics',
                )}
                placeholder={
                  form.values.clinicIds.length === 0
                    ? t('carePlan.fields.allClinics', 'All clinics')
                    : undefined
                }
                data={facilityOptions}
                searchable
                clearable
                {...form.getInputProps('clinicIds')}
              />
              <Select
                label={t('carePlan.fields.therapeuticIndication', 'Therapeutic indication')}
                withAsterisk
                data={indicationOptions}
                searchable
                placeholder={t('carePlan.fields.selectTherapeuticIndication', 'Select an indication')}
                {...form.getInputProps('therapeuticIndicationID')}
              />
              <TextInput
                label={t('carePlan.fields.planType', 'Plan type')}
                placeholder={t('carePlan.fields.planTypePlaceholder', 'e.g. Surgery, Onboarding')}
                {...form.getInputProps('planType')}
              />
            </Group>

            <Group grow align="flex-end">
              <TextInput
                label={t('carePlan.fields.eventLabelEn', 'Event label (EN)')}
                placeholder={t('carePlan.fields.eventLabelEnPlaceholder', 'e.g. Surgery date')}
                {...form.getInputProps('eventLabelEn')}
              />
              <TextInput
                label={t('carePlan.fields.eventLabelFr', 'Event label (FR)')}
                placeholder={t('carePlan.fields.eventLabelFrPlaceholder', 'e.g. Date de chirurgie')}
                {...form.getInputProps('eventLabelFr')}
              />
              <Switch
                mt="md"
                label={t('carePlan.fields.eventDateRequired', 'Event date required')}
                {...form.getInputProps('eventDateRequired', { type: 'checkbox' })}
              />
            </Group>

            <Textarea
              label={t('carePlan.fields.descriptionEn', 'Description (EN)')}
              autosize
              minRows={2}
              {...form.getInputProps('descriptionEn')}
            />
            <Textarea
              label={t('carePlan.fields.descriptionFr', 'Description (FR)')}
              autosize
              minRows={2}
              {...form.getInputProps('descriptionFr')}
            />

            <CarePlanTasksConfig
              value={form.values.tasks}
              onChange={(tasks) => form.setFieldValue('tasks', tasks)}
              questionnairesData={questionnaires ?? []}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={closeForm}>
                {t('button.cancel', 'Cancel')}
              </Button>
              <Button type="submit" loading={createTemplate.isPending || updateTemplate.isPending}>
                {t('button.save', 'Save')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
        <Title c="black" order={4}>
          {t('carePlan.listTitle', 'Care plans')}
        </Title>
        <Button
          leftSection={<IconPlus />}
          onClick={openCreate}
          styles={{ label: { align: 'center', justifyContent: 'center' } }}
        >
          {t('carePlan.create', 'New care plan')}
        </Button>
      </Flex>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : isError ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" color="red" size={44} radius="xl">
              <IconInbox size={22} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              {t('carePlan.loadError')}
            </Text>
          </Stack>
        </Center>
      ) : (templates ?? []).length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <ThemeIcon variant="light" color="gray" size={44} radius="xl">
              <IconInbox size={22} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              {t('carePlan.empty', 'No care plans yet.')}
            </Text>
          </Stack>
        </Center>
      ) : (
        <TableContextProvider>
          <CarePlanTemplatesTable
            templates={templates ?? []}
            indicationName={indicationName}
            clinicNames={clinicNames}
            statusOptions={statusOptions}
            onEdit={openEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
          />
        </TableContextProvider>
      )}
    </Stack>
  );
};

interface ICarePlanTemplatesTableProps {
  templates: ICarePlanTemplate[];
  indicationName: (id: number) => string;
  clinicNames: (ids: number[]) => string;
  statusOptions: { value: string; label: string }[];
  onEdit: (template: ICarePlanTemplate) => void;
  onDelete: (template: ICarePlanTemplate) => void;
  onStatusChange: (
    template: ICarePlanTemplate,
    status: CarePlanTemplateStatus,
  ) => void;
}

const CarePlanTemplatesTable: React.FC<ICarePlanTemplatesTableProps> = ({
  templates,
  indicationName,
  clinicNames,
  statusOptions,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } =
    useTable();

  useEffect(() => {
    setSortedData(
      tableTools.sortData([...templates], {
        sortBy: 'nameEn',
        reversed: false,
        search: '',
      }),
    );
  }, [templates, setSortedData]);

  return (
    <Table.ScrollContainer minWidth={760}>
      <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Th
              sorted={sortBy === 'nameEn'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('nameEn', sortedData)}
            >
              {t('carePlan.fields.nameEn', 'Name (EN)')}
            </Th>
            <Table.Th>{t('carePlan.fields.therapeuticIndication', 'Therapeutic indication')}</Table.Th>
            <Table.Th>{t('carePlan.fields.clinics', 'Clinics')}</Table.Th>
            <Th
              sorted={sortBy === 'planType'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('planType', sortedData)}
            >
              {t('carePlan.fields.planType', 'Plan type')}
            </Th>
            <Table.Th>{t('carePlan.tasks.title', 'Care plan tasks')}</Table.Th>
            <Th
              sorted={sortBy === 'status'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('status', sortedData)}
            >
              {t('carePlan.fields.status', 'Status')}
            </Th>
            <Table.Th style={{ textAlign: 'right' }}> </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedData.map((template: ICarePlanTemplate) => (
            <Table.Tr key={template.id}>
              <Table.Td>
                <Text fz="sm" fw={500}>
                  {template.nameEn}
                </Text>
                <Text fz="xs" c="dimmed">
                  {template.nameFr}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz="sm">{indicationName(template.therapeuticIndicationID)}</Text>
              </Table.Td>
              <Table.Td>
                <Text
                  fz="sm"
                  c={template.healthcareFacilityIDs.length === 0 ? 'dimmed' : undefined}
                >
                  {clinicNames(template.healthcareFacilityIDs)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz="sm" c={template.planType ? undefined : 'dimmed'}>
                  {template.planType ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz="sm">{template.tasks.length}</Text>
              </Table.Td>
              <Table.Td>
                <Select
                  aria-label={t('carePlan.fields.status', 'Status')}
                  data={statusOptions}
                  value={template.status}
                  allowDeselect={false}
                  onChange={(val) => {
                    if (isCarePlanTemplateStatus(val))
                      onStatusChange(template, val);
                  }}
                  w={150}
                />
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Tooltip label={t('button.edit', 'Edit')}>
                    <ActionIcon
                      variant="subtle"
                      aria-label={t('button.edit', 'Edit')}
                      onClick={() => onEdit(template)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('button.delete', 'Delete')}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('button.delete', 'Delete')}
                      onClick={() => onDelete(template)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

export default CarePlanTemplatesPage;
